import { forwardRef, memo, type ReactNode } from 'react';
import size from 'lodash/size';
import trim from 'lodash/trim';
import isString from 'lodash/isString';
import clsx from 'clsx';
import { FormattedMessage } from 'react-intl';
// Material UI imports
import CardHeader, { type CardHeaderProps } from '@mui/material/CardHeader';
import Divider from '@mui/material/Divider';
// local imports
import { Values } from '../helpers/intl';
import BoxTypography from '../mixins/BoxTypography';
import CardSubheader from './CardSubheader';
// SCSS imports
import { wrapped, nonwrapped, noTopPadding, noBottomPadding, vDivider, hDivider, displayFlex } from './CardTitle.module.scss';

export type CardTitleProps = {
  readonly title: ReactNode | string;
  readonly values?: Values;
  readonly withVerticalDivider?: boolean;
  readonly subheader?: ReactNode | string;
  readonly avatar?: ReactNode;
  readonly action?: ReactNode;
  readonly withDivider?: boolean;
  readonly compact?: boolean;
  readonly following?: boolean;
  readonly centered?: boolean;
  readonly wrap?: boolean;
  readonly flex?: boolean;
  readonly disabled?: boolean;
  readonly className?: string;
  readonly sx?: CardHeaderProps['sx'];
};

const CardTitle = forwardRef<HTMLElement, CardTitleProps>(({
  title,
  values,
  flex = false,
  withVerticalDivider = false,
  subheader,
  avatar,
  action,
  withDivider = false,
  compact = false,
  following = false,
  centered = false,
  wrap = false,
  disabled = false,
  className,
  sx
}, ref) => (
  <>
    <CardHeader
        ref={ref}
        sx={sx}
        disableTypography
        title={
          <BoxTypography
              pr={1.5}
              flexGrow={centered ? 1 : undefined}
              align={centered ? 'center' : undefined}
              variant="h3"
              color={disabled ? 'text.disabled' : undefined}
              className={clsx({
                [vDivider]: withVerticalDivider,
                [displayFlex]: flex
              })}
          >
            {isString(title) && size(title) >= 1 ? (
              <FormattedMessage id={title} values={values} defaultMessage={title}/>
            ) : title}
          </BoxTypography>
        }
        subheader={isString(subheader) ? size(trim(subheader)) >= 1 && (
          <CardSubheader
              text={subheader}
              values={values}
              alignSelf="flex-end"
              pl={3}
              pb={0.125}
          />
        ) || undefined : subheader}
        avatar={avatar}
        action={action}
        className={clsx(className, {
          [wrapped]: wrap,
          [nonwrapped]: !wrap,
          [noTopPadding]: following,
          [noBottomPadding]: compact
        })}
    />
    {withDivider ? <Divider light className={hDivider}/> : undefined}
  </>
));

CardTitle.displayName = 'CardTitle';

export default memo(CardTitle);
